<script setup lang="ts">
import type { SpotlightContent, SpotlightLink } from '~kui/components/KUI/Unified/Spotlight/KUIUnifiedSpotlight.vue';
import type { OverviewActiveCampaign } from '~kui/types/learningCampaign';

import { Button } from '~ds/components/ui/button';
import { Heading, Text } from '~ds/components/ui/typography';
import KUICoreInputRichTextEditorRenderer from '~kui/components/KUI/Core/Input/RichTextEditor/Renderer/KUICoreInputRichTextEditorRenderer.vue';
import { RTE_RENDERER_VARIANT } from '~kui/components/KUI/Core/Input/RichTextEditor/Renderer/types';
import KUIUnifiedSpotlight from '~kui/components/KUI/Unified/Spotlight/KUIUnifiedSpotlight.vue';
import KUIUnifiedSkillItem from '~kui/components/KUI/Unified/Wallet/Skill/KUIUnifiedSkillItem.vue';
import { DEFAULT_ANYWAY_PARTNER_BG_COLOR } from '~kui/constants/anyway';
import { useI18n } from 'vue-i18n';

import KUIUniPrizeOverview from './KUIUniPrizeOverview.vue';

export type BadgeColorToken = 'lime' | 'purple' | 'coral' | 'pink' | 'blue' | 'orange' | 'yellow';

export interface OverviewBadge {
  name: string;
  bgColor?: BadgeColorToken | null;
  badgeUrl?: string | null;
  premiumBadgeUrl?: string | null;
  background?: string | null;
}

export interface OverviewSkill {
  id: string;
  name: string;
  iconUrl?: string | null;
}

export interface OverviewLearningOutcome {
  title: string;
  description?: string | null;
  assetId?: string | null;
  iconUrl?: string | null;
}

export interface Props {
  title: string;
  isAuthenticated: boolean;
  termsTo: string;
  partnerName?: string | null;
  description?: string | null;
  coverImageUrl?: string | null;
  logoImageUrl?: string | null;
  badge?: OverviewBadge | null;
  coins?: number | null;
  completedCount?: number | null;
  totalCount?: number | null;
  duration?: number | null;
  learningOutcomes?: OverviewLearningOutcome[];
  hasStructuredOutcomes?: boolean;
  completedAssetIds?: Set<string>;
  skills?: OverviewSkill[];
  hasProgress?: boolean;
  activeCampaign?: OverviewActiveCampaign | null;
  hasCompletedAccount?: boolean;
  confirmPrizeLoading?: boolean;
  confirmPrizeError?: string | null;
  partnerBgColor?: string;
  startLoading?: boolean;
  alertTitle?: string;
  alertAriaLabel?: string;
  alertDescription?: string;
  // Shifts the leading cover badge clear of a back button overlaid at the top-left.
  hasBackButton?: boolean;
  spotlight?: SpotlightContent | null;
  flushTop?: boolean;
}

enum OverviewTab {
  LEARNING_OUTCOMES = 'learningOutcomes',
  SKILLS = 'skills',
  PRIZE_ENTRY = 'prizeEntry',
}

const props = withDefaults(defineProps<Props>(), {
  description: null,
  partnerName: '',
  coverImageUrl: null,
  logoImageUrl: null,
  badge: null,
  coins: null,
  completedCount: null,
  totalCount: null,
  duration: null,
  learningOutcomes: () => [],
  hasStructuredOutcomes: false,
  completedAssetIds: () => new Set<string>(),
  skills: () => [],
  hasProgress: false,
  activeCampaign: null,
  hasCompletedAccount: false,
  confirmPrizeLoading: false,
  confirmPrizeError: null,
  flushTop: false,
  partnerBgColor: DEFAULT_ANYWAY_PARTNER_BG_COLOR,
  startLoading: false,
  alertTitle: '',
  alertDescription: '',
  alertAriaLabel: '',
  hasBackButton: false,
  spotlight: null,
});

defineEmits<{
  start: [];
  enterLearningCampaign: [skillAnswer: string | null];
  verifyProfile: [];
  spotlightLinkClick: [link: SpotlightLink];
}>();

const { t } = useI18n();

const selectedOutcome = ref<OverviewLearningOutcome | null>(null);
const outcomeDialogOpen = ref(false);

const openOutcomeDialog = (outcome: OverviewLearningOutcome) => {
  selectedOutcome.value = outcome;
  outcomeDialogOpen.value = true;
};

const descriptionIsRichText = computed(() => {
  if (!props.description) {
    return false;
  }
  try {
    const parsed = JSON.parse(props.description);
    return parsed?.type === 'doc';
  }
  catch {
    return false;
  }
});

const hasBadge = computed(() => {
  return !!(props.badge && props.badge.name && props.badge.badgeUrl && (props.badge.bgColor || props.badge.background));
});

const badgeGradientClasses = cva('', {
  variants: {
    color: {
      lime: 'bg-gradient-to-b from-secondary-subtle to-secondary',
      purple: 'bg-gradient-to-b from-brand-purple-subtle to-brand-purple',
      coral: 'bg-gradient-to-b from-brand-coral-subtle to-brand-coral',
      pink: 'bg-gradient-to-b from-brand-pink-subtle to-brand-pink',
      blue: 'bg-gradient-to-b from-brand-blue-subtle to-brand-blue',
      orange: 'bg-gradient-to-b from-brand-orange-subtle to-brand-orange',
      yellow: 'bg-gradient-to-b from-brand-yellow-subtle to-brand-yellow',
    },
  },
});

const badgeClasses = computed(() => {
  const base = 'flex items-center gap-4 rounded-2xl px-4 py-4 shadow-lg flex-col lg:flex-row text-center lg:text-start';

  if (props.badge?.bgColor) {
    return twMerge(base, badgeGradientClasses({ color: props.badge.bgColor }));
  }

  return base;
});

const badgeBackgroundStyle = computed(() => {
  if (props.badge?.background) {
    return {
      backgroundImage: `url(${props.badge.background})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
    };
  }
  return {};
});

// A prizeless campaign has nothing to enter, so its block renders inline above the tabs
// instead of under a "Prize entry" tab.
const hasPrize = computed(() => props.activeCampaign?.hasPrize ?? false);

const prizeOverviewProps = computed(() => ({
  isAuthenticated: props.isAuthenticated,
  hasCompletedAccount: props.hasCompletedAccount,
  termsTo: props.termsTo,
  alertTitle: props.alertTitle,
  alertDescription: props.alertDescription,
  alertAriaLabel: props.alertAriaLabel,
  confirmPrizeLoading: props.confirmPrizeLoading,
  confirmPrizeError: props.confirmPrizeError,
  faqs: props.activeCampaign?.faqs,
}));

const tabs = computed(() => {
  const opts = [];

  if (props.activeCampaign && hasPrize.value) {
    opts.push({
      value: OverviewTab.PRIZE_ENTRY,
      label: t('unified.activity.overview.tabs.prizeEntry'),
    });
  }

  if (props.learningOutcomes.length > 0) {
    opts.push({
      value: OverviewTab.LEARNING_OUTCOMES,
      label: t('unified.activity.overview.tabs.learningOutcomes'),
    });
  }

  if (props.skills.length > 0) {
    opts.push({
      value: OverviewTab.SKILLS,
      label: t('unified.activity.overview.tabs.skills'),
    });
  }

  return opts;
});

const activeTab = ref<string | undefined>(undefined);

watch(tabs, (newTabs) => {
  if (!activeTab.value && newTabs.length > 0) {
    activeTab.value = newTabs[0]?.value;
  }
}, { immediate: true });

// A campaign arriving (or being swapped for a different one) promotes its tab. Keyed on the id
// rather than the object: `activeCampaign` is an object prop, so watching it directly also fires
// when the parent re-renders and passes the same campaign in a fresh object, which would yank the
// user off whichever tab they had just picked.
watch(() => props.activeCampaign?.id, (campaignId) => {
  if (campaignId && hasPrize.value) {
    activeTab.value = OverviewTab.PRIZE_ENTRY;
  }
}, { immediate: true });

const badgeImageClasses = computed(() => {
  return twMerge(
    cva('pointer-events-none size-36 object-contain rounded-full select-none', {
      variants: {
        earned: {
          true: '',
          false: 'grayscale opacity-95 mix-blend-luminosity-screen',
        },
      },
    })({
      earned: props.completedCount !== null && props.totalCount !== null && props.completedCount >= props.totalCount,
    }),
  );
});
</script>

<template>
  <div class="flex h-full flex-col bg-transparent rounded-t-3xl md:rounded-t-none">
    <div class="flex-1 overflow-y-auto">
      <!-- Hero cover image — matches the content column width below -->
      <div
        v-if="coverImageUrl"
        class="relative mx-auto w-full max-w-2xl px-4 sm:px-6"
        :class="flushTop ? 'pt-0' : 'pt-4 sm:pt-6'"
      >
        <NuxtImg
          :src="coverImageUrl"
          :alt="title"
          class="h-56 w-full object-cover sm:h-64 md:h-72 lg:h-80 rounded-2xl sm:rounded-3xl shadow-lg"
        />
      </div>

      <div
        class="mx-auto flex w-full max-w-2xl flex-col gap-6 px-4 pb-4 sm:px-6 sm:pb-6"
        :class="flushTop && !coverImageUrl ? 'pt-0' : 'pt-4 sm:pt-6'"
      >
        <div class="flex gap-3">
          <div v-if="logoImageUrl" class="flex size-14 shrink-0 items-center justify-center rounded-full bg-muted p-2">
            <div class="flex size-full items-center justify-center rounded-full">
              <NuxtImg
                v-if="logoImageUrl"
                :src="logoImageUrl"
                :alt="title"
                class="size-full object-cover "
              />
            </div>
          </div>
          <div class="flex flex-col gap-1 lg:justify-center lg:w-full">
            <div class="flex flex-col lg:flex-row gap-2 lg:items-center justify-between">
              <Heading
                size="lg"
                as="h1"
                class="leading-none"
              >
                {{ title }}
              </Heading>
              <div
                v-if="completedCount != null && totalCount != null && totalCount > 0"
                class="flex items-center gap-1.5 rounded-full bg-secondary px-3 py-1 shrink-0 w-fit"
                data-testid="overview-progress-badge"
              >
                <Text size="sm" class="text-foreground font-medium">
                  {{ completedCount }} / {{ totalCount }}
                </Text>

                <KUIUnifiedActivityBadge
                  class="shrink-0"
                  size="xs"
                  :icon-url="logoImageUrl"
                  completed
                  :partner-bg-color="partnerBgColor"
                />
              </div>
              <!-- Coin badge (single LA) -->
              <div v-else-if="coins && coins > 0" class="flex items-center gap-1.5 rounded-full bg-secondary px-3 py-1.5">
                <NuxtImg
                  src="/images/kui/unified/gold-coin.webp"
                  alt="Coins"
                  class="size-5"
                />
                <Text size="md" class="text-foreground">
                  {{ coins.toLocaleString() }}
                </Text>
              </div>
            </div>
            <KUICoreInputRichTextEditorRenderer
              v-if="description && descriptionIsRichText"
              :content="description"
              :variant="RTE_RENDERER_VARIANT.PARAGRAPH"
              class="text-muted-foreground"
            />
            <Text
              v-else-if="description"
              size="md"
              class="text-muted-foreground"
            >
              {{ description }}
            </Text>
          </div>
        </div>

        <div
          v-if="hasBadge"
          :class="badgeClasses"
          :style="badgeBackgroundStyle"
        >
          <div class="shrink-0">
            <NuxtImg
              :src="badge!.badgeUrl!"
              :alt="badge!.name"
              :class="badgeImageClasses"
            />
          </div>
          <div class="flex min-w-0 flex-col gap-0.5 text-foreground">
            <Heading
              size="2xl"
              as="h2"
              class="xs:text-4xl sm:text-5xl leading-none"
            >
              {{ badge?.name }}
            </Heading>
            <Text
              v-if="partnerName"
              size="sm"
              class="mt-1"
            >
              {{ t('unified.activity.overview.by', { name: partnerName }) }}
            </Text>
          </div>
        </div>

        <KUIUniPrizeOverview
          v-if="activeCampaign && !hasPrize"
          v-bind="prizeOverviewProps"
          :active-campaign="activeCampaign"
          @enter-learning-campaign="$emit('enterLearningCampaign', $event)"
          @verify-profile="$emit('verifyProfile')"
        />

        <KUICoreTabs
          v-if="tabs.length > 0"
          v-model="activeTab"
          :tabs="tabs"
          tab-to-content-gap-size="lg"
          position="start"
        >
          <template #learningOutcomes>
            <!-- Learning outcomes -->
            <div v-if="learningOutcomes.length > 0" class="flex flex-col gap-3">
              <Heading size="md" as="h2">
                {{ t('unified.activity.overview.learningObjectives') }}
              </Heading>
              <div class="flex-col gap-2 grid grid-cols-1">
                <component
                  :is="hasStructuredOutcomes ? 'button' : 'div'"
                  v-for="(outcome, index) in learningOutcomes"
                  :key="index"
                  class="flex w-full items-center justify-between rounded-xl bg-muted px-4 py-3"
                  :class="{ 'cursor-pointer': hasStructuredOutcomes }"
                  @click="hasStructuredOutcomes ? openOutcomeDialog(outcome) : undefined"
                >
                  <Text size="sm" class="my-auto text-start">
                    {{ outcome.title }}
                  </Text>

                  <KUIUnifiedActivityBadge
                    size="md"
                    :icon-url="outcome.iconUrl"
                    :completed="!!outcome.assetId && completedAssetIds.has(outcome.assetId)"
                    :partner-bg-color="partnerBgColor"
                    incomplete-background-variant="dark"
                  />
                </component>
              </div>
            </div>

            <!-- Structured learning outcome dialog -->
            <KUICoreDialog
              v-model="outcomeDialogOpen"
              :heading="selectedOutcome?.title"
              content-width="fit"
              has-overlay
              to="body"
            >
              <KUICoreInputRichTextEditorRenderer
                v-if="selectedOutcome?.description && typeof selectedOutcome.description === 'string'"
                :content="selectedOutcome.description"
                :variant="RTE_RENDERER_VARIANT.PARAGRAPH"
              />
            </KUICoreDialog>
          </template>

          <!-- skills -->
          <template #skills>
            <div v-if="skills.length > 0" class="flex flex-col gap-3">
              <Heading size="md" as="h2">
                {{ t('unified.activity.overview.skills') }}
              </Heading>
              <div class="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 justify-items-center bg-muted p-4 rounded-2xl gap-4">
                <KUIUnifiedSkillItem
                  v-for="skill in skills"
                  :key="skill.id"
                  :name="skill.name"
                  :icon-url="skill.iconUrl"
                  :earned="true"
                />
              </div>
            </div>
          </template>

          <!-- prize -->
          <template #prizeEntry>
            <KUIUniPrizeOverview
              v-if="activeCampaign"
              v-bind="prizeOverviewProps"
              :active-campaign="activeCampaign"
              @enter-learning-campaign="$emit('enterLearningCampaign', $event)"
              @verify-profile="$emit('verifyProfile')"
            />
          </template>
        </KUICoreTabs>

        <KUIUnifiedSpotlight
          v-if="spotlight"
          :heading="spotlight.heading"
          :image-url="spotlight.imageUrl"
          :image-alt="spotlight.imageAlt"
          :links="spotlight.links"
          @link-click="$emit('spotlightLinkClick', $event)"
        />
      </div>
      <slot />
    </div>

    <!-- Start button — sticky at viewport bottom -->
    <div class="sticky bottom-0 mx-auto w-full max-w-2xl shrink-0 bg-transparent py-4 sm:p-6">
      <Button
        variant="secondary"
        size="lg"
        :loading="startLoading"
        data-testid="overview-start-button"
        class="w-full"
        type="button"
        @click="$emit('start')"
      >
        {{ hasProgress ? t('unified.activity.overview.continue') : t('unified.activity.overview.start') }}
      </Button>
    </div>
  </div>
</template>
